'use client';

/**
 * Faithful CSS recreations of the Reclipt UI for marketing mockups, themed to
 * the Stitch "Void & Neon" palette. Static by design (no backend).
 *
 * TODO(screenshots): swap inner content for real captured screenshots of the
 * live chat-search / library pages when available; keep the surrounding frames.
 */

import { Play, Bot, Send, Download } from 'lucide-react';

const thumbGradients = [
  'from-amber-500/30 to-rose-600/40',
  'from-cyan-500/25 to-blue-700/40',
  'from-violet-500/30 to-fuchsia-700/40',
  'from-emerald-500/25 to-teal-700/40',
  'from-rose-500/30 to-pink-700/40',
];

/** A clip tile for the edge-to-edge marquee gallery. */
export function GalleryCard({
  label,
  tags,
  gradient,
}: {
  label: string;
  tags: string[];
  gradient: string;
}) {
  return (
    <div className="group w-[300px] flex-shrink-0 overflow-hidden rounded-3xl border border-white/10 bg-white/[0.05] backdrop-blur-2xl">
      <div className={`relative aspect-video bg-gradient-to-br ${gradient}`}>
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity group-hover:opacity-100">
          <Play className="h-12 w-12 text-white" fill="currentColor" />
        </div>
      </div>
      <div className="p-4">
        <p className="mb-1 font-mk-label text-xs font-bold uppercase tracking-[0.1em] text-mk-pink">
          {label}
        </p>
        <div className="flex gap-2">
          {tags.map((t) => (
            <span
              key={t}
              className="rounded border border-white/10 bg-mk-s px-2 py-0.5 text-[10px] text-mk-muted"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/** The "Talk to your footage" chat mockup: query, AI answer, a MomentCard. */
export function ChatSearchMock() {
  return (
    <div className="space-y-6">
      {/* user query */}
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-2xl rounded-tr-none bg-mk-pink px-4 py-2 font-mk-body text-sm text-mk-on-pink">
          Find the moment where they talk about the budget.
        </div>
      </div>

      {/* AI answer + MomentCard */}
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-white/10 bg-mk-s-highest">
          <Bot className="h-4 w-4 text-mk-pink" />
        </div>
        <div className="w-full space-y-4">
          <p className="inline-block rounded-2xl rounded-tl-none bg-mk-s px-4 py-2 font-mk-body text-sm text-mk-muted">
            Found it. Here is the segment from the Q3 meeting.
          </p>

          {/* MomentCard */}
          <div className="w-full max-w-sm overflow-hidden rounded-2xl border border-mk-pink/30 bg-white/[0.05] backdrop-blur-2xl">
            <div className="group relative aspect-video bg-gradient-to-br from-slate-600/40 to-slate-900/60">
              <div className="absolute bottom-2 right-2 rounded bg-black/60 px-2 py-0.5 font-mk-label text-[10px]">
                00:12:45
              </div>
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors group-hover:bg-black/0">
                <Play className="h-10 w-10 text-white" fill="currentColor" />
              </div>
            </div>
            <div className="flex items-center justify-between bg-mk-s-high p-3">
              <div className="flex flex-col">
                <span className="text-xs font-bold text-mk-ink">
                  Q3_Budget_Review.mp4
                </span>
                <span className="text-[10px] text-mk-muted">
                  Speaker: Sarah Jennings
                </span>
              </div>
              <button
                className="text-mk-pink transition-transform hover:scale-110"
                aria-label="Download clip"
              >
                <Download className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* input bar */}
      <div className="mt-8 flex items-center gap-3 border-t border-white/10 pt-4">
        <div className="flex-1 rounded-full bg-mk-s px-4 py-2 font-mk-body text-sm text-mk-muted">
          Ask about another moment…
        </div>
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-mk-pink text-mk-on-pink">
          <Send className="h-4 w-4" />
        </div>
      </div>
    </div>
  );
}

export { thumbGradients };
